import {
  withAuthInfo,
  useRedirectFunctions,
  useLogoutFunction,
  WithAuthInfoProps,
  useHostedPageUrls,
} from "@propelauth/react";
import { useAuthFrontendApis } from "@propelauth/frontend-apis-react";
import { LoginState } from "@propelauth/frontend-apis";
import { getAuthUrl } from "@fuse/utils/environement";
import { useForm, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Typography,
  Link,
  TextField,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
} from "@mui/material";
import FuseSvgIcon from "@fuse/core/FuseSvgIcon";
import { styled } from "@mui/material/styles";
import React, { useState } from "react";
import Logo from "../theme-layouts/shared-components/LogoDark";
import LanguageSwitcher from "../theme-layouts/shared-components/LanguageSwitcher";

const StyledBox = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "100vh",
  padding: theme.spacing(2),
  backgroundColor: "#f7f7f7",
}));

const FormContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  maxWidth: 400,
  padding: theme.spacing(4),
  borderRadius: "1rem",
  backgroundColor: "#ffffff",
  border: "1px solid #e0e0e0",
  boxShadow: "none",
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  display: "flex",
  justifyContent: "center",
  "& img": {
    maxHeight: 80,
    width: "auto",
  },
}));

interface SignInFormData {
  email: string;
  password: string;
}

const SignModule = withAuthInfo((props: WithAuthInfoProps) => {
  const { t } = useTranslation("signInPage");
  const logoutFunction = useLogoutFunction();
  const { redirectToSignupPage, redirectToAccountPage } =
    useRedirectFunctions();
  const { getLoginPageUrl } = useHostedPageUrls();
  const { emailPasswordLogin } = useAuthFrontendApis();
  const [loading, setLoading] = useState(false);
  const [authError, setAuthError] = useState<string>("");
  const [showPassword, setShowPassword] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<SignInFormData>({
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const handleLogOut = () => {
    logoutFunction(true);
  };

  const handleForgotPassword = () => {
    // Use the login page URL with a forgot password parameter
    window.location.href = getLoginPageUrl() + "?forgot_password=true";
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const mapErrorMessage = (error: any): string => {
    // This function now serves as a fallback for the catch block
    // Most errors should be handled by the specific PropelAuth response handlers above

    const errorMessage = error?.message?.toLowerCase() || "";
    const errorCode = error?.code || error?.error_code || "";

    // Network and server errors (most common in catch block)
    if (
      errorMessage.includes("network") ||
      errorMessage.includes("connection") ||
      errorCode === "network_error"
    ) {
      return t("NETWORK_ERROR");
    }

    if (errorMessage.includes("timeout") || errorCode === "timeout") {
      return t("CONNECTION_TIMEOUT");
    }

    if (
      errorMessage.includes("server_error") ||
      errorMessage.includes("internal_server_error") ||
      errorCode === "server_error" ||
      error?.status >= 500
    ) {
      return t("SERVER_ERROR");
    }

    if (
      errorMessage.includes("service_unavailable") ||
      errorMessage.includes("maintenance") ||
      errorCode === "service_unavailable" ||
      error?.status === 503
    ) {
      return t("SERVICE_UNAVAILABLE");
    }

    // Authentication fallbacks (in case PropelAuth response handlers don't catch everything)
    if (
      errorMessage.includes("invalid_credentials") ||
      errorMessage.includes("invalid email or password") ||
      errorCode === "invalid_credentials"
    ) {
      return t("INVALID_CREDENTIALS");
    }

    if (
      errorMessage.includes("user_not_found") ||
      errorCode === "user_not_found"
    ) {
      return t("ACCOUNT_NOT_FOUND");
    }

    if (
      errorMessage.includes("account_locked") ||
      errorCode === "account_locked"
    ) {
      return t("ACCOUNT_LOCKED");
    }

    if (
      errorMessage.includes("account_disabled") ||
      errorCode === "account_disabled"
    ) {
      return t("ACCOUNT_DISABLED");
    }

    // Default fallback for unexpected errors
    return t("UNKNOWN_ERROR");
  };

  const onSubmit = async (data: SignInFormData) => {
    setLoading(true);
    setAuthError("");

    try {
      const response = await emailPasswordLogin({
        email: data.email,
        password: data.password,
      });

      await response.handle({
        success: (data) => {
          // Handle different login states
          switch (data.login_state) {
            case LoginState.LOGGED_IN:
              // User is successfully logged in
              window.location.href = "/";
              break;
            case LoginState.TWO_FACTOR_REQUIRED:
              // Handle 2FA requirement
              setAuthError(t("TWO_FACTOR_REQUIRED"));
              // You could redirect to a 2FA page here if you have one
              // window.location.href = '/two-factor-auth';
              break;
            case LoginState.EMAIL_NOT_CONFIRMED_YET:
              setAuthError(t("EMAIL_VERIFICATION_REQUIRED"));
              // Optionally offer to resend verification email
              break;
            case LoginState.USER_MISSING_REQUIRED_PROPERTIES:
              // User needs to complete their profile
              setAuthError(t("PROFILE_INCOMPLETE"));
              // You could redirect to profile completion page
              // window.location.href = '/complete-profile';
              break;
            case "OrgCreationRequired":
              // User needs to create an organization
              setAuthError(t("ORG_CREATION_REQUIRED"));
              // Redirect to PropelAuth organization page after a short delay
              setTimeout(() => {
                redirectToAccountPage();
              }, 2000);
              break;
            default:
              console.warn("Unknown login state:", data.login_state);
              setAuthError(t("UNKNOWN_ERROR"));
          }
        },
        // Handle invalid email or password - this is the key fix
        invalidCredentials: () => {
          console.error("Invalid credentials provided");
          setAuthError(t("INVALID_CREDENTIALS"));
        },
        // Handle disabled user account
        userAccountDisabled: () => {
          console.error("User account is disabled");
          setAuthError(t("ACCOUNT_DISABLED"));
        },
        // Handle locked user account
        userAccountLocked: () => {
          console.error("User account is locked");
          setAuthError(t("ACCOUNT_LOCKED"));
        },
        // Handle password login disabled
        passwordLoginDisabled: () => {
          console.error("Password login is disabled");
          setAuthError(t("PASSWORD_LOGIN_DISABLED"));
        },
        // Handle unexpected or unhandled errors
        unexpectedOrUnhandled: (error) => {
          console.error("Unexpected login error:", error);
          setAuthError(t("UNKNOWN_ERROR"));
        },
        // Keep badRequest as fallback for actual bad request scenarios (malformed data, etc.)
        badRequest: (error) => {
          console.error("Bad request error:", error);

          // Handle PropelAuth user_facing_errors structure
          if (error.user_facing_errors) {
            // Check for field-specific errors first
            if (error.user_facing_errors.email) {
              setAuthError(error.user_facing_errors.email);
              return;
            }
            // Handle other field errors by using the first available error
            const fieldErrors = Object.values(error.user_facing_errors);
            if (fieldErrors.length > 0) {
              setAuthError(fieldErrors[0]);
              return;
            }
          }

          // Handle single user_facing_error
          if (error.user_facing_error) {
            setAuthError(error.user_facing_error);
            return;
          }

          // Handle field_errors (alternative PropelAuth error structure)
          if (error.field_errors) {
            const fieldErrors = Object.values(error.field_errors);
            if (fieldErrors.length > 0) {
              setAuthError(fieldErrors[0]);
              return;
            }
          }

          // Fallback to generic bad request message
          setAuthError(t("BAD_REQUEST"));
        },
      });
    } catch (error) {
      console.error("Sign in error:", error);
      setAuthError(mapErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  if (props.isLoggedIn) {
    return (
      <StyledBox>
        <FormContainer>
          <Typography variant="h5" component="h1" gutterBottom align="center">
            Welcome, {props.user.username}
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 3 }}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              size="large"
              onClick={() => redirectToAccountPage()}
            >
              Account
            </Button>
            <Button
              variant="outlined"
              color="error"
              fullWidth
              size="large"
              onClick={handleLogOut}
            >
              Logout
            </Button>
          </Box>
        </FormContainer>
      </StyledBox>
    );
  }

  return (
    <StyledBox>
      <Box sx={{ position: "absolute", top: 16, right: 16 }}>
        <LanguageSwitcher />
      </Box>

      <LogoContainer>
        <Logo />
      </LogoContainer>

      <Typography
        variant="h2"
        component="h1"
        gutterBottom
        align="center"
        sx={{
          fontWeight: "bold",
          fontSize: "2rem",
          color: "#0f0f0f",
          mb: 3,
        }}
      >
        {t("SIGN_IN_TITLE")}
      </Typography>

      <FormContainer>
        {authError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {authError}
          </Alert>
        )}

        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          sx={{ width: "100%" }}
        >
          <Controller
            name="email"
            control={control}
            rules={{
              required: t("EMAIL_REQUIRED"),
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: t("EMAIL_INVALID"),
              },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label={t("EMAIL_LABEL")}
                type="email"
                fullWidth
                margin="normal"
                error={!!errors.email}
                helperText={errors.email?.message}
                disabled={loading}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "0.5rem",
                    height: "48px",
                    "& fieldset": {
                      borderColor: "#e0e0e0",
                    },
                    "&:hover fieldset": {
                      borderColor: "#006783",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#006783",
                      borderWidth: "2px",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "#666666",
                    transform: "translate(14px, 14px) scale(1)",
                    "&.Mui-focused, &.MuiFormLabel-filled": {
                      color: "#006783",
                      transform: "translate(14px, -9px) scale(0.75)",
                    },
                  },
                }}
              />
            )}
          />

          <Controller
            name="password"
            control={control}
            rules={{
              required: t("PASSWORD_REQUIRED"),
              minLength: {
                value: 8,
                message: t("PASSWORD_MIN_LENGTH"),
              },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label={t("PASSWORD_LABEL")}
                type={showPassword ? "text" : "password"}
                fullWidth
                margin="normal"
                error={!!errors.password}
                helperText={errors.password?.message}
                disabled={loading}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleTogglePasswordVisibility}
                        edge="end"
                        sx={{
                          color: "#666666",
                          "&:hover": {
                            color: "#006783",
                          },
                        }}
                      >
                        {showPassword ? (
                          <FuseSvgIcon size={20}>
                            material-outline:visibility_off
                          </FuseSvgIcon>
                        ) : (
                          <FuseSvgIcon size={20}>
                            material-outline:visibility
                          </FuseSvgIcon>
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "0.5rem",
                    height: "48px",
                    "& fieldset": {
                      borderColor: "#e0e0e0",
                    },
                    "&:hover fieldset": {
                      borderColor: "#006783",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#006783",
                      borderWidth: "2px",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "#666666",
                    transform: "translate(14px, 14px) scale(1)",
                    "&.Mui-focused, &.MuiFormLabel-filled": {
                      color: "#006783",
                      transform: "translate(14px, -9px) scale(0.75)",
                    },
                  },
                }}
              />
            )}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            size="large"
            disabled={loading}
            sx={{
              mt: 1.5,
              mb: 2,
              height: "48px",
              borderRadius: "0.5rem",
              backgroundColor: "#006783",
              fontWeight: "bold",
              textTransform: "uppercase",
              "&:hover": {
                backgroundColor: "#005366",
              },
              "&:disabled": {
                backgroundColor: "#cccccc",
              },
            }}
          >
            {loading ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1, color: "white" }} />
                {t("SIGNING_IN")}
              </>
            ) : (
              t("SIGN_IN_BUTTON")
            )}
          </Button>

          <Box sx={{ display: "flex", justifyContent: "center", mb: 0 }}>
            <Link
              component="button"
              type="button"
              variant="body2"
              onClick={handleForgotPassword}
              sx={{
                textDecoration: "none",
                color: "#006783",
                "&:hover": {
                  textDecoration: "underline",
                },
              }}
            >
              {t("FORGOT_PASSWORD")}
            </Link>
          </Box>
        </Box>
      </FormContainer>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: 1,
          mt: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {t("NO_ACCOUNT")}
        </Typography>
        <Link
          component="button"
          type="button"
          variant="body2"
          onClick={() => redirectToSignupPage()}
          sx={{
            textDecoration: "none",
            color: "#006783",
            "&:hover": {
              textDecoration: "underline",
            },
          }}
        >
          {t("SIGN_UP_LINK")}
        </Link>
      </Box>
    </StyledBox>
  );
});

export default SignModule;
